import type { ComponentTransform } from "building-types/components/componentTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { OptionDefinitions } from "building-types/options/optionDefinitions";
import type {
  EvaluatedRoof,
  Roof,
  RoofBlocks,
  RoofOpeningPlacement,
  RoofSystem,
  SkylightPlacement,
} from "building-types/roofs/roofSystemTypes";
import type { RWTable, Table } from "common/data/table";
import { groupBy } from "common/utils/arrays";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { instanceIdForComponent } from "doc/entity/componentInstanceEntityResolve";
import { evaluateEntityForComponentInstance } from "doc/formula/evalFormulas";
import { isVirtualRoofBlock, roofBlockIds } from "doc/roofSystemTools";
import { applyTransformToOutlineAndEdgeIds } from "../../utils/componentTransforms";
import { shouldGenerateEntity } from "../../utils/entityIsolation";
import { componentInstanceLevelId } from "./componentInstanceLevel";

function hasParentId<T extends { parentId?: string }>(item: T): item is T & { readonly parentId: string } {
  return !!item.parentId;
}

/**
 * Groups roofs by their parent component IDs so each componentId maps directly
 * to the roofs that belong to it, avoiding a separate collection pass + re-filter.
 */
function groupRoofsByComponentId(
  roofsInComponent: EvaluatedRoof[],
  idToParents: Table<readonly string[]>,
): Map<string, EvaluatedRoof[]> {
  const roofsByComponent = new Map<string, EvaluatedRoof[]>();
  for (const roof of roofsInComponent) {
    for (const cid of idToParents[roof.id] || []) {
      let list = roofsByComponent.get(cid);
      if (!list) {
        list = [];
        roofsByComponent.set(cid, list);
      }
      list.push(roof);
    }
  }
  return roofsByComponent;
}

/**
 * Adds component instance copies of evaluated roof systems and roofs so that roofs
 * that live in components get generated per instance. Each instance copy has blocks
 * remapped to instance block IDs, so they pass the blockPerimeters filter and get
 * generated in instance space.
 *
 * The roof system's and roof's `disabled` conditions, plus roof openings and skylights,
 * are evaluated per instance so instance option choices show/hide them; entries resolving
 * to `disabled: true` are omitted. `disabled` is re-evaluated from `rawRoofSystemsInput`
 * and `rawRoofsInput` because the archetype-resolved input copies have already collapsed
 * formulas to a single value; all other fields are reused from those archetype copies.
 */
export function addRoofComponentInstances(
  roofSystemsInput: Table<RoofSystem>,
  roofsInput: Table<EvaluatedRoof>,
  rawRoofSystemsInput: Table<RoofSystem>,
  rawRoofsInput: Table<Roof>,
  idToParents: Table<readonly string[]>,
  componentToTransforms: Table<readonly ComponentTransform[]>,
  levels: Table<Level>,
  isolatedEntity: DocEntityHandle | undefined,
  roofOpeningsInput: Table<RoofOpeningPlacement>,
  skylightsInput: Table<SkylightPlacement>,
  definitions: OptionDefinitions,
  rawOptionChoices: OptionChoices,
): {
  roofSystems: RWTable<RoofSystem>;
  roofs: RWTable<EvaluatedRoof>;
  roofOpenings: RWTable<RoofOpeningPlacement>;
  skylights: RWTable<SkylightPlacement>;
} {
  const roofSystems: RWTable<RoofSystem> = { ...roofSystemsInput };
  const roofs: RWTable<EvaluatedRoof> = { ...roofsInput };
  const roofOpenings: RWTable<RoofOpeningPlacement> = { ...roofOpeningsInput };
  const skylights: RWTable<SkylightPlacement> = { ...skylightsInput };

  const roofsByRoofSystem = groupBy(Object.values(roofsInput), (r) => r.parentId);
  const openingsByRoof = groupBy(Object.values(roofOpeningsInput).filter(hasParentId), (o) => o.parentId);
  const skylightsByRoof = groupBy(Object.values(skylightsInput).filter(hasParentId), (s) => s.parentId);

  for (const roofSystemId in roofSystemsInput) {
    const roofSystem = roofSystemsInput[roofSystemId];
    if (!roofSystem) {
      continue;
    }
    const roofsInSystem = roofsByRoofSystem[roofSystemId] || [];
    const roofsInComponent = roofsInSystem.filter((r) => (idToParents[r.id] || []).length > 0);
    if (roofsInComponent.length === 0) {
      continue;
    }

    const roofsByComponent = groupRoofsByComponentId(roofsInComponent, idToParents);

    for (const [componentId, componentRoofs] of roofsByComponent) {
      const transforms = componentToTransforms[componentId] || [];
      for (const transform of transforms) {
        if (!shouldGenerateEntity(transform.instanceId, idToParents, isolatedEntity)) {
          continue;
        }
        const chain = transform.componentInstanceChain;
        const instanceRoofSystemId = instanceIdForComponent(roofSystemId, chain);

        const rawRoofSystem = rawRoofSystemsInput[roofSystemId];
        assertIsNotUndefined(rawRoofSystem, "rawRoofSystem");
        const instanceRoofSystemDisabled = evaluateEntityForComponentInstance(
          { disabled: rawRoofSystem.disabled },
          chain,
          definitions,
          rawOptionChoices,
        ).disabled;
        if (instanceRoofSystemDisabled) {
          continue;
        }

        for (const roof of componentRoofs) {
          const isVirtual = isVirtualRoofBlock(roof.blocks);
          const blockIds = roofBlockIds(roof.blocks);
          if (blockIds.length === 0 && !isVirtual) {
            continue;
          }

          // Re-evaluate `disabled` per instance from the raw roof, whose formula the
          // archetype-evaluated `roof` has already collapsed to a single value.
          const rawRoof = rawRoofsInput[roof.id];
          const instanceDisabled = rawRoof
            ? evaluateEntityForComponentInstance({ disabled: rawRoof.disabled }, chain, definitions, rawOptionChoices)
                .disabled
            : roof.disabled;
          if (instanceDisabled) {
            continue;
          }

          const instanceRoofId = instanceIdForComponent(roof.id, chain);
          let instanceBlocks: RoofBlocks | undefined;

          if (isVirtual) {
            const virtualBlock = roof.blocks;
            const { transformedOutline, transformedEdgeIds } = applyTransformToOutlineAndEdgeIds(
              transform,
              virtualBlock.perimeter,
              virtualBlock.edgeIds,
            );
            instanceBlocks = {
              ...virtualBlock,
              perimeter: transformedOutline,
              edgeIds: transformedEdgeIds,
              levelUsage: {
                ...virtualBlock.levelUsage,
                entityId: componentInstanceLevelId(
                  virtualBlock.levelUsage.entityId,
                  levels,
                  transform.verticalPlacement,
                  chain,
                ),
              },
            };
          } else {
            instanceBlocks = blockIds.map((blockId) => instanceIdForComponent(blockId, chain));
          }

          const instanceRoof: EvaluatedRoof = {
            ...roof,
            id: instanceRoofId,
            parentId: instanceRoofSystemId,
            blocks: instanceBlocks,
            disabled: instanceDisabled,
            componentInstanceChain: chain,
            componentTransform: transform,
          };
          roofs[instanceRoofId] = instanceRoof;

          // Add roof openings for this roof instance.
          const openings = openingsByRoof[roof.id] || [];
          for (const opening of openings) {
            const evaluatedOpening = evaluateEntityForComponentInstance(opening, chain, definitions, rawOptionChoices);
            if (evaluatedOpening.disabled) {
              continue;
            }
            const instanceOpeningId = instanceIdForComponent(opening.id, chain);
            roofOpenings[instanceOpeningId] = {
              ...evaluatedOpening,
              id: instanceOpeningId,
              parentId: instanceRoofId,
            };
          }

          // Add skylights for this roof instance.
          const roofSkylights = skylightsByRoof[roof.id] || [];
          for (const skylight of roofSkylights) {
            const evaluatedSkylight = evaluateEntityForComponentInstance(
              skylight,
              chain,
              definitions,
              rawOptionChoices,
            );
            if (evaluatedSkylight.disabled) {
              continue;
            }
            const instanceSkylightId = instanceIdForComponent(skylight.id, chain);
            skylights[instanceSkylightId] = {
              ...evaluatedSkylight,
              id: instanceSkylightId,
              parentId: instanceRoofId,
            };
          }
        }

        roofSystems[instanceRoofSystemId] = {
          ...roofSystem,
          id: instanceRoofSystemId,
          disabled: instanceRoofSystemDisabled,
        };
      }
    }
  }

  return { roofSystems, roofs, roofOpenings, skylights };
}
